<template>
  <div
    v-if="shouldRender"
    class="tw-flex tw-flex-col tw-gap-2"
    data-testid="insight-metrics-section"
  >
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
      <h3 class="tw-text-sm tw-font-medium tw-text-sf-neutral-dark-1 tw-m-0">
        {{ I18n.t('report.drawer.insight.section_title') }}
      </h3>
      <span v-if="!readOnly && !fullscreen" v-tooltip="createMetricTooltip">
        <SfButtonDs
          variant="secondary"
          size="sm"
          icon-before="plus"
          :disabled="store.locked"
          data-testid="insight-metrics-create-metric"
          @click="openCreateMetric"
        >
          {{ I18n.t('report.drawer.insight.create_metric') }}
        </SfButtonDs>
      </span>
    </div>

    <CreateMetricModal v-if="!readOnly && !store.locked" />

    <div
      v-if="fullscreen"
      class="tw-grid tw-grid-cols-2 tw-gap-2"
      data-testid="insight-metrics-fullscreen-grid"
    >
      <InsightMetricPill
        v-for="entry in store.insight"
        :key="`fullscreen-${entry.id}`"
        :entry="entry"
        :expanded="true"
        :hide-collapse="true"
        :read-only="readOnly"
      />
    </div>

    <template v-else>
      <InsightMetricPill
        v-for="entry in expanded"
        :key="`expanded-${entry.id}`"
        :entry="entry"
        :expanded="true"
        :read-only="readOnly"
        @update:expanded="(value) => onToggle(entry.id, value)"
      />

      <Draggable
        v-if="rowEntries.length > 0"
        v-model="rowEntries"
        class="tw-grid tw-grid-cols-2 tw-gap-2"
        item-key="id"
        handle=".handle"
        :animation="200"
        :disabled="readOnly || store.locked"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element: entry }">
          <InsightMetricPill
            :entry="entry"
            :expanded="false"
            :read-only="readOnly"
            :dragging="dragging"
            @update:expanded="(value) => onToggle(entry.id, value)"
          />
        </template>
      </Draggable>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Draggable from 'vuedraggable'

import CreateMetricModal from '@app/application/report/components/graphs-drawer/create-metric-modal.vue'
import InsightMetricPill from '@app/application/report/components/graphs-drawer/insight-metric-pill.vue'
import { useExpandedSet } from '@app/application/report/composables/use-expanded-set'
import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import type { DrawerGraphEntry } from '@app/application/report/types'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({
  name: 'InsightMetricsSection',
})

interface Props {
  readOnly?: boolean
  fullscreen?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  readOnly: false,
  fullscreen: false,
})

const I18n = useI18n()
const store = useReportGraphsDrawer()

const dragging = ref(false)

const { expanded, row, onToggle } = useExpandedSet(() => store.insight)

// Mirrors the composable's `row` into a writable ref because vuedraggable
// mutates its v-model during a drag. Resyncs whenever `row` changes
// (collapse, server patch, rollback).
const rowEntries = ref<DrawerGraphEntry[]>([])
watch(
  row,
  (next) => {
    rowEntries.value = [...next]
  },
  { immediate: true }
)

const shouldRender = computed(
  () => store.insight.length > 0 || (!props.readOnly && !store.locked)
)

const createMetricTooltip = computed(() =>
  store.locked ? I18n.t('report.drawer.locked_tooltip') : ''
)

function openCreateMetric(): void {
  window.modalCitizen.openModalPortal('reports/create-metric-modal')
}

function onDragEnd(): void {
  dragging.value = false

  // Build the full ordered id list: expanded entries keep their original
  // position; slim entries are taken from the row in their dragged order.
  const expandedIds = new Set(expanded.value.map((entry) => entry.id))
  const newSlimIds = rowEntries.value.map((entry) => entry.id)
  const fullOrder: number[] = []
  let slimIdx = 0
  for (const entry of store.insight) {
    if (expandedIds.has(entry.id)) {
      fullOrder.push(entry.id)
    } else {
      fullOrder.push(newSlimIds[slimIdx] ?? entry.id)
      slimIdx += 1
    }
  }

  const currentIds = store.insight.map((entry) => entry.id)
  const unchanged = fullOrder.every((id, i) => id === currentIds[i])
  if (unchanged) return

  void store.reorderInsight(fullOrder)
}
</script>
